import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager, prepareRequest } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, HeaderTitle, Input, Label, Select } from '@atlas/ui';

import PaymentMethodItem from 'components/ClientProfile/PaymentTab/PaymentMethodItem';

import { displayWithdrawalSuccessNotification } from '../withdrawalUtils';

const BANK_IDENTITY_OPTIONS = [
  {
    text: 'Akbank',
    value: 'BANK_AKBANK',
  },
  {
    text: 'Albaraka Turk',
    value: 'BANK_ALBARAKA_TURK',
  },
  {
    text: 'Denizbank',
    value: 'BANK_DENIZBANK',
  },
  {
    text: 'FAST EFT',
    value: 'BANK_FAST_EFT',
  },
  {
    text: 'Fibabank',
    value: 'BANK_FIBABANK',
  },
  {
    text: 'Garanti Bankasi',
    value: 'BANK_GARANTI_BANKASI',
  },
  {
    text: 'Halkbank',
    value: 'BANK_HALKBANK',
  },
  {
    text: 'iNG Bank',
    value: 'BANK_ING_BANK',
  },
  {
    text: 'is Bankasi',
    value: 'BANK_IS_BANKASI',
  },
  {
    text: 'Finansbank / Enpara',
    value: 'BANK_FINANSBANK_ENPARA',
  },
  {
    text: 'Kuveyt Turk',
    value: 'BANK_KUVEYT_TURK',
  },
  {
    text: 'Payfix',
    value: 'BANK_PAYFIX',
  },
  {
    text: 'Sekerbank',
    value: 'BANK_SEKERBANK',
  },
  {
    text: 'TEB',
    value: 'BANK_TEB',
  },
  {
    text: 'Turkiye Finans',
    value: 'BANK_TURKIYE_FINANS',
  },
  {
    text: 'Vakif Katilim',
    value: 'BANK_VAKIF_KATILIM',
  },
  {
    text: 'Yapi Kredi',
    value: 'BANK_YAPI_KREDI',
  },
  {
    text: 'Ziraat Bankasi',
    value: 'BANK_ZIRAAT_BANKASI',
  },
];

export const WithdrawalInterkassaPopup = ({ data, id }) => {
  const { fields, formAction, name, paymentMethod, pmId, redirectUrl } = data;
  const parsedData = useMemo(
    () => parseWithdrawalQuery(redirectUrl, { parseNumbers: false }),
    [redirectUrl],
  );
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [bankIdentity, setBankIdentity] = useState(BANK_IDENTITY_OPTIONS[0].value);
  const [iban, setIban] = useState('');
  const [error, setError] = useState(null);
  const isVegaPaymentMethod = name === 'vega';

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  const validateIBAN = useCallback(value => {
    const error = {
      iban: null,
    };

    if (
      !value.match(
        /^([A-Z]{2}[ '+'\\'+'-]?[0-9]{2})(?=(?:[ '+'\\'+'-]?[A-Z0-9]){9,30}$)((?:[ '+'\\'+'-]?[A-Z0-9]{3,5}){2,7})([ '+'\\'+'-]?[A-Z0-9]{1,3})?$/,
      )
    ) {
      error.iban = {
        message: 'incorrect_iban',
      };
    }

    return error;
  }, []);

  const validateIfsc = useCallback(
    value => {
      const error = {
        ifsc: null,
      };

      if (isVegaPaymentMethod && !value) {
        error.ifsc = {
          message: 'mandatory_field',
        };
      } else if (!value.match(/^[a-zA-Z0-9+_@.-]+$/)) {
        error.ifsc = {
          message: 'incorrect_ifsc',
        };
      }

      return error;
    },
    [isVegaPaymentMethod],
  );

  const validateAccountNumber = useCallback(
    value => {
      const error = {
        account_number: null,
      };

      if (isVegaPaymentMethod && !value) {
        error.account_number = {
          message: 'mandatory_field',
        };
      } else if (!value.match(/^[a-zA-Z0-9]{6,40}$/)) {
        error.account_number = {
          message: 'incorrect_account_number',
        };
      }

      return error;
    },
    [isVegaPaymentMethod],
  );

  const validateDocumentId = useCallback(
    value => {
      const error = {
        document_id: null,
      };

      const isMefetePaymentMethod = name === 'mefete';
      if (
        (isMefetePaymentMethod && !value.match(/^[1-9]{1}[0-9]{9}[02468]{1}$/)) ||
        (!isMefetePaymentMethod && !value.match(/^[0-9]{11}$/))
      ) {
        error.document_id = {
          message: 'incorrect_document_id',
        };
      }

      return error;
    },
    [name],
  );

  const handleChange = useCallback(
    (event, cb, validateFn) => {
      cb(event.target.value);
      setError({
        ...error,
        ...validateFn(event.target.value),
      });
    },
    [error],
  );

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;
    const request = prepareRequest({
      method: 'POST',
      url: formAction,
    });

    const { amount, currency, email, first_name, last_name, paymentId, phone } = parsedData;

    const requestData = {
      additionalParameters: {
        email,
        first_name,
        last_name,
        publicPaymentId: paymentId,
      },
      amount,
      currency,
      pmId,
    };

    const error = {
      ...(fields.includes('iban') && validateIBAN(iban)),
      ...(fields.includes('account_number') && validateAccountNumber(accountNumber)),
      ...(fields.includes('document_id') && validateDocumentId(documentId)),
      ...(fields.includes('ifsc') && validateIfsc(ifsc)),
    };

    if (Object.values(error).some(i => i)) {
      return setError(error);
    }

    if (fields.includes('phone')) {
      requestData.additionalParameters.phone = `${phone}`.trim();
    }

    if (fields.includes('iban')) {
      requestData.additionalParameters.iban = iban;
    }

    if (fields.includes('account_number')) {
      requestData.additionalParameters.account_number = accountNumber;
    }

    if (fields.includes('document_id')) {
      requestData.additionalParameters.document_id = documentId;
    }

    if (fields.includes('bank_identity')) {
      requestData.additionalParameters.bank_identity = bankIdentity;
    }

    if (fields.includes('ifsc')) {
      requestData.additionalParameters.ifsc = ifsc;
    }

    setIsSubmitting(true);

    try {
      const response = await request(requestData);
      displayWithdrawalSuccessNotification({ amount, currency, paymentMethod, response });
    } catch (error) {
      NotificationManager.display({
        data: {
          headerTitle: `withdrawal_by_${name}`,
          title: 'failed_sad_emoticon',
        },
        type: 'error',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    formAction,
    parsedData,
    pmId,
    fields,
    validateIBAN,
    iban,
    validateAccountNumber,
    accountNumber,
    validateDocumentId,
    documentId,
    validateIfsc,
    ifsc,
    bankIdentity,
    paymentMethod,
    name,
    handleClose,
  ]);

  return (
    <DefaultPopup
      className="withdrawal-pix"
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <HeaderTitle className="block-big withdrawal-pix__title">
        <PaymentMethodItem
          amount={parseInt(parsedData.amount)}
          paymentMethod={paymentMethod}
          showAmount
        />
      </HeaderTitle>

      <div className="withdrawal-pix__content">
        {fields.includes('account_number') && (
          <>
            <Input
              active={isActiveInput === 'account_number'}
              className="withdrawal-pix__content-item"
              error={error?.account_number}
              isRequiredSymbol={isVegaPaymentMethod}
              label="account_number"
              onBlur={() => setIsActiveInput(false)}
              onChange={e => handleChange(e, setAccountNumber, validateAccountNumber)}
              onFocus={() => setIsActiveInput('account_number')}
              type="text"
              value={accountNumber}
            />
            {error?.account_number && (
              <div className="help-text-container">
                <Label
                  message={error.account_number.message}
                  params={error.account_number.params}
                />
              </div>
            )}
          </>
        )}

        {fields.includes('document_id') && (
          <>
            <Input
              active={isActiveInput === 'document_id'}
              className="withdrawal-pix__content-item"
              error={error?.document_id}
              label="document_id"
              onBlur={() => setIsActiveInput(false)}
              onChange={e => handleChange(e, setDocumentId, validateDocumentId)}
              onFocus={() => setIsActiveInput('document_id')}
              type="text"
              value={documentId}
            />
            {error?.document_id && (
              <div className="help-text-container">
                <Label message={error.document_id.message} params={error.document_id.params} />
              </div>
            )}
          </>
        )}

        {fields.includes('bank_identity') && (
          <Select
            active={isActiveInput === 'bank_identity'}
            className="withdrawal-bitcoin__select withdrawal-pix__content-item"
            label="bank_identity"
            onBlur={() => setIsActiveInput(false)}
            onChange={setBankIdentity}
            onFocus={() => setIsActiveInput('bank_identity')}
            options={BANK_IDENTITY_OPTIONS}
          />
        )}

        {fields.includes('iban') && (
          <>
            <Input
              active={isActiveInput === 'iban'}
              className="withdrawal-pix__content-item"
              error={error?.iban}
              label="iban"
              onBlur={() => setIsActiveInput(false)}
              onChange={e => handleChange(e, setIban, validateIBAN)}
              onFocus={() => setIsActiveInput('iban')}
              type="text"
              value={iban}
            />
            {error?.iban && (
              <div className="help-text-container">
                <Label message={error.iban.message} params={error.iban.params} />
              </div>
            )}
          </>
        )}

        {fields.includes('ifsc') && (
          <>
            <Input
              active={isActiveInput === 'ifsc'}
              className="withdrawal-pix__content-item"
              error={error?.ifsc}
              isRequiredSymbol={isVegaPaymentMethod}
              label="ifsc"
              onBlur={() => setIsActiveInput(false)}
              onChange={e => handleChange(e, setIfsc, validateIfsc)}
              onFocus={() => setIsActiveInput('ifsc')}
              type="text"
              value={ifsc}
            />
            {error?.ifsc && (
              <div className="help-text-container">
                <Label message={error.ifsc.message} params={error.ifsc.params} />
              </div>
            )}
          </>
        )}

        <Button
          className="withdrawal-pix__button"
          disabled={(error && Object.values(error).some(i => i)) || isSubmitting}
          label="withdrawal"
          loading={isSubmitting}
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};

WithdrawalInterkassaPopup.propTypes = {
  data: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.object),
    formAction: PropTypes.string,
    name: PropTypes.string,
    paymentMethod: PropTypes.string,
    pmId: PropTypes.string,
    redirectUrl: PropTypes.string,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
